import {
  GpOwnFundDdqQuota,
  Subscription,
  SubscriptionAddon,
  UsageStatistics,
} from '@core/models/subscription.model';

/** Raw `accounts/profile` subscription block (camelCase or snake_case). */
export type ProfileSubscriptionPayload = Record<string, unknown> | null | undefined;

export function extractProfileSubscriptionPayload(profileResp: unknown): ProfileSubscriptionPayload {
  if (!profileResp || typeof profileResp !== 'object') {
    return null;
  }
  const p = profileResp as Record<string, unknown>;
  return (p['subscription'] as ProfileSubscriptionPayload) ?? null;
}

export function mapProfileAddon(addon: unknown): SubscriptionAddon | null {
  if (!addon || typeof addon !== 'object') {
    return null;
  }
  const a = addon as Record<string, unknown>;
  const uid = String(a['uid'] ?? '').trim();
  const name = String(a['name'] ?? '').trim();
  if (!uid || !name) {
    return null;
  }
  const addonType = (a['addonType'] ?? a['addon_type'] ?? 'placement_agent') as SubscriptionAddon['addonType'];
  return {
    uid,
    code: String(a['code'] ?? ''),
    name,
    description: String(a['description'] ?? ''),
    addonType,
    isActive: true,
  };
}

/** Purchased GP add-ons from profile (`activeAddons` / `active_addons`). */
export function mapActiveAddonsFromProfile(rawSub: ProfileSubscriptionPayload): SubscriptionAddon[] {
  if (!rawSub) {
    return [];
  }
  const list = (rawSub['activeAddons'] ?? rawSub['active_addons']) as unknown;
  if (!Array.isArray(list)) {
    return [];
  }
  return list.map(mapProfileAddon).filter((a): a is SubscriptionAddon => a != null);
}

function mapProfileTier(rawSub: ProfileSubscriptionPayload): Subscription['tier'] | null {
  if (!rawSub?.['tier'] || typeof rawSub['tier'] !== 'object') {
    return null;
  }
  const t = rawSub['tier'] as Record<string, unknown>;
  const uid = String(t['uid'] ?? '').trim();
  const code = String(t['code'] ?? '').trim();
  const name = String(t['name'] ?? '').trim();
  if (!uid && !code) {
    return null;
  }
  const tierType = (t['tierType'] ?? t['tier_type'] ?? 'gp') as 'gp' | 'lp';
  return {
    uid: uid || code,
    code: code || uid,
    name: name || code,
    description: t['description'] != null ? String(t['description']) : undefined,
    tierType,
  };
}

function mapProfileUsage(rawSub: ProfileSubscriptionPayload): UsageStatistics | null {
  const rawUsage = rawSub?.['usage'];
  if (!rawUsage || typeof rawUsage !== 'object') {
    return null;
  }
  const u = rawUsage as Record<string, unknown>;
  const now = new Date();
  return {
    ddqRunsThisMonth: Number(u['ddqRunsThisMonth'] ?? u['ddq_runs_this_month'] ?? 0),
    remainingDdqRuns:
      u['remainingDdqRuns'] != null
        ? (u['remainingDdqRuns'] as number | null)
        : u['remaining_ddq_runs'] != null
          ? (u['remaining_ddq_runs'] as number | null)
          : null,
    imGenerationsTotal: Number(u['imGenerationsTotal'] ?? u['im_generations_total'] ?? 0),
    canGenerateIm:
      u['canGenerateIm'] !== undefined
        ? Boolean(u['canGenerateIm'])
        : u['can_generate_im'] !== undefined
          ? Boolean(u['can_generate_im'])
          : undefined,
    month: typeof u['month'] === 'number' ? u['month'] : now.getMonth() + 1,
    year: typeof u['year'] === 'number' ? u['year'] : now.getFullYear(),
  };
}

function mapGpQuotaFromProfileUsage(rawSub: ProfileSubscriptionPayload): GpOwnFundDdqQuota | undefined {
  const rawUsage = rawSub?.['usage'];
  if (!rawUsage || typeof rawUsage !== 'object') {
    return undefined;
  }
  const u = rawUsage as Record<string, unknown>;
  const raw = u['gpOwnFundDdq'] ?? u['gp_own_fund_ddq'];
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }
  const o = raw as Record<string, unknown>;
  const limit = o['limit'];
  if (typeof limit !== 'number') {
    return undefined;
  }
  return {
    limit,
    usedThisMonth: Number(o['usedThisMonth'] ?? o['used_this_month'] ?? 0),
    remaining: Number(o['remaining'] ?? 0),
    month: typeof o['month'] === 'number' ? o['month'] : new Date().getMonth() + 1,
    year: typeof o['year'] === 'number' ? o['year'] : new Date().getFullYear(),
  };
}

/**
 * Merge profile subscription (tier + activeAddons + LP usage) into an existing store subscription,
 * or build a minimal GP/LP skeleton when `/current/` has not been loaded yet.
 */
export function mergeProfileIntoSubscription(
  current: Subscription | null,
  rawSub: ProfileSubscriptionPayload,
): Subscription | null {
  if (!rawSub) {
    return current;
  }

  const activeAddons = mapActiveAddonsFromProfile(rawSub);
  const tier = mapProfileTier(rawSub);
  const usage = mapProfileUsage(rawSub);
  const gpOwnFundDdqQuota = mapGpQuotaFromProfileUsage(rawSub);

  if (!tier && activeAddons.length === 0 && !usage) {
    return current;
  }

  if (current) {
    return {
      ...current,
      tier: tier ?? current.tier,
      activeAddons: activeAddons.length > 0 ? activeAddons : current.activeAddons ?? [],
      addons: activeAddons.length > 0 ? activeAddons : current.addons,
      usage: usage ?? current.usage,
      gpOwnFundDdqQuota: gpOwnFundDdqQuota ?? current.gpOwnFundDdqQuota,
    };
  }

  if (!tier) {
    return null;
  }

  return {
    uid: '',
    tier,
    activeAddons,
    addons: activeAddons,
    usage: usage ?? undefined,
    gpOwnFundDdqQuota: gpOwnFundDdqQuota ?? null,
    startDate: '',
    isActive: true,
    status: 'active',
  };
}
